import { useState, useEffect, useMemo } from "react";
import { usePaginatedLeads, applyLeadFilters } from "@/hooks/usePaginatedLeads";
import { updateLeadRecord } from "@/hooks/useLeads";
import { useProfile } from "@/hooks/useProfile";
import { useCalendar } from "@/hooks/useCalendar";
import { useTeamRole } from "@/hooks/useTeamRole";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import { useAuditLog } from "@/hooks/useAuditLog";
import { can } from "@/lib/permissions";
import { logActivity } from "@/lib/staffActivity";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { DbLead, STATUS_OPTIONS, getFitTier } from "@/lib/types";
import { openExternalUrl } from "@/lib/validation";
import LeadDrawer from "@/components/LeadDrawer";
import LeadScoreBreakdown from "@/components/LeadScoreBreakdown";
import FitTierBadge from "@/components/FitTierBadge";
import { Search, Eye, Mail, Calendar, Flame, Zap, X, UserPlus, AlertTriangle, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

function getScoreTier(score: number) {
  if (score >= 85) return { label: score === 100 ? "Emergency" : "Hot", bg: "bg-hot/10 text-hot", border: "border-l-hot" };
  if (score >= 50) return { label: "Warm", bg: "bg-warning/10 text-warning", border: "border-l-warning" };
  return { label: "Cold", bg: "bg-muted text-muted-foreground", border: "border-l-muted-foreground" };
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export default function LeadsPage() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { provider: calendarProvider } = useCalendar();
  const { teamRole, agencyId } = useTeamRole();
  const { members } = useTeamMembers();
  const { logAction } = useAuditLog();
  const seg = (profile as any)?.segment || "homecare";

  const canAssign = teamRole && can(teamRole, "updateAnyLead");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterScore, setFilterScore] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [selectedLead, setSelectedLead] = useState<DbLead | null>(null);
  const [exporting, setExporting] = useState(false);

  const PER_PAGE = 10;

  // Debounce search so every keystroke doesn't trigger a server round-trip.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { leads: paged, totalCount, loading, refetch } = usePaginatedLeads({
    page,
    perPage: PER_PAGE,
    search: debouncedSearch,
    filterScore,
    filterStatus,
    filterAssignee,
    sortField,
    sortDir,
  });

  const memberMap = useMemo(() => {
    const m: Record<string, { name: string; initials: string }> = {};
    members.forEach(mem => {
      const name = mem.owner_name || mem.agency_email || "Unknown";
      const initials = name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
      m[mem.id] = { name, initials };
    });
    return m;
  }, [members]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PER_PAGE));

  const anyFilterActive = search || filterScore !== "all" || filterStatus !== "all" || filterAssignee !== "all";

  function handleSort(field: string) {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
    setPage(0);
  }

  function clearFilters() {
    setSearch("");
    setDebouncedSearch("");
    setFilterScore("all");
    setFilterStatus("all");
    setFilterAssignee("all");
    setPage(0);
  }

  // Exports every row matching the current filters, not just the loaded page \u2014
  // pagination only holds PER_PAGE rows in memory, so this re-queries the server.
  const EXPORT_CAP = 10000;

  async function exportCsv() {
    if (totalCount === 0) {
      toast.info("No leads to export");
      return;
    }
    setExporting(true);
    try {
      let query = supabase.from("leads").select("*");
      query = applyLeadFilters(query, { search: debouncedSearch, filterScore, filterStatus, filterAssignee });
      const { data, error } = await query
        .order(sortField, { ascending: sortDir === "asc" })
        .range(0, EXPORT_CAP - 1);
      if (error) throw error;
      const rowsData = data ?? [];

      const headers = [
        "Name", "ZIP", "Score", "Tier", "Urgency", "Hours/Week",
        "Payer Source", "Care Needs", "Status", "Assigned To",
        "Enriched", "Created At",
      ];
      const escape = (v: unknown) => {
        const s = v === null || v === undefined ? "" : String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = rowsData.map(l => {
        const tier = getScoreTier(l.score).label;
        const assignedId = (l as any).assigned_to as string | null;
        const assignedName = assignedId ? memberMap[assignedId]?.name ?? "" : "";
        return [
          l.name,
          l.zip_code ?? "",
          l.score,
          tier,
          l.urgency ?? "",
          l.hours_needed ?? "",
          l.payer_source ?? "",
          l.acuity_notes ?? "",
          l.status,
          assignedName,
          l.enriched ? "Yes" : "No",
          new Date(l.created_at).toISOString(),
        ].map(escape).join(",");
      });
      const csv = "\uFEFF" + [headers.join(","), ...rows].join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `leads-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rowsData.length} lead${rowsData.length === 1 ? "" : "s"}${rowsData.length === EXPORT_CAP ? ` (capped at ${EXPORT_CAP})` : ""}`);
      logAction("export", "leads", undefined, {
        count: rowsData.length,
        filters: { search: debouncedSearch, filterScore, filterStatus, filterAssignee },
        format: "csv",
      });
    } catch (err: any) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  async function assignLead(leadId: string, assigneeId: string | null) {
    try {
      const { error } = await supabase.from("leads").update({ assigned_to: assigneeId }).eq("id", leadId);
      if (error) throw error;

      if (assigneeId && user && agencyId) {
        await supabase.from("lead_assignments").insert({
          lead_id: leadId,
          assigned_to: assigneeId,
          assigned_by: user.id,
        });
        logActivity(user.id, agencyId, "lead_assigned", leadId);
      }
      logAction(
        assigneeId ? "lead_assigned" : "lead_unassigned",
        "lead",
        leadId,
        { assigned_to: assigneeId },
      );
      toast.success(assigneeId ? "Lead assigned" : "Lead unassigned");
      refetch();
    } catch (err: any) {
      toast.error(err.message || "Failed to assign lead");
    }
  }

  function isStaleHotLead(lead: DbLead): boolean {
    if (lead.score < 85) return false;
    if (lead.status !== "new") return false;
    if ((lead as any).assigned_to) return false;
    return Date.now() - new Date(lead.created_at).getTime() > ONE_DAY_MS;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gold border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full max-w-full overflow-hidden">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={seg === "grouphome" ? "Search inquiries..." : "Search leads..."}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="pl-9"
          />
        </div>
        <Select value={filterScore} onValueChange={v => { setFilterScore(v); setPage(0); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Scores" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scores</SelectItem>
            <SelectItem value="100">Emergency (100)</SelectItem>
            <SelectItem value="hot">Hot (85–99)</SelectItem>
            <SelectItem value="warm">Warm (50–84)</SelectItem>
            <SelectItem value="cold">Cold (0–49)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={v => { setFilterStatus(v); setPage(0); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {canAssign && (
          <Select value={filterAssignee} onValueChange={v => { setFilterAssignee(v); setPage(0); }}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Assigned To" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {members.map(m => (
                <SelectItem key={m.id} value={m.id}>{memberMap[m.id]?.name || "—"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {anyFilterActive && (
          <button onClick={clearFilters} className="text-sm font-medium text-gold hover:underline flex items-center gap-1">
            <X className="h-3 w-3" /> Clear
          </button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={exportCsv}
          className="ml-auto gap-1.5"
          disabled={totalCount === 0 || exporting}
          title="Download the currently filtered leads as a CSV file"
        >
          <Download className="h-4 w-4" />
          {exporting ? "Exporting…" : "Download CSV"}
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        {paged.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <div className="h-16 w-16 rounded-full border-2 border-dashed flex items-center justify-center mb-3">
              <Search className="h-6 w-6" />
            </div>
            <p className="text-sm mb-3">No {seg === "grouphome" ? "inquiries" : "leads"} match your filters</p>
            <Button size="sm" variant="outline" onClick={clearFilters} className="text-gold border-gold">Clear Filters</Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="w-6 px-2"></th>
                    {[
                      { key: "name", label: "Name" },
                      { key: "zip_code", label: seg === "grouphome" ? "Locality" : "ZIP" },
                      { key: "score", label: "Score" },
                      { key: "fit_tier", label: "Fit" },
                      { key: "acuity_notes", label: seg === "grouphome" ? "Population" : "Specialty" },
                      { key: "payer_source", label: seg === "grouphome" ? "Waiver" : "Payer" },
                      { key: "status", label: "Status" },
                      { key: "assigned_to", label: "Assigned To" },
                      { key: "enriched", label: "Enriched" },
                      { key: "created_at", label: "Date" },
                    ].map(col => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className={`text-left px-4 py-2.5 font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground ${
                          sortField === col.key ? "text-gold border-b-2 border-gold" : ""
                        } ${["acuity_notes", "payer_source"].includes(col.key) ? "hidden sm:table-cell" : ""} ${
                          col.key === "enriched" || col.key === "created_at" ? "hidden md:table-cell" : ""
                        } ${col.key === "fit_tier" ? "hidden md:table-cell" : ""}`}
                      >
                        {col.label} {sortField === col.key && (sortDir === "asc" ? "↑" : "↓")}
                      </th>
                    ))}
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(lead => {
                    const tier = getScoreTier(lead.score);
                    const assignedId = (lead as any).assigned_to as string | null;
                    const assignee = assignedId ? memberMap[assignedId] : null;
                    const stale = isStaleHotLead(lead);
                    return (
                      <tr
                        key={lead.id}
                        onClick={() => setSelectedLead(lead)}
                        className={`border-b border-l-4 ${tier.border} hover:bg-muted/30 cursor-pointer transition-colors`}
                      >
                        <td className="px-2 py-3 align-middle">
                          {stale && (
                            <span title="Unassigned hot lead — assign to a team member">
                              <AlertTriangle className="h-4 w-4 text-warning" />
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-navy">
                          <span className="flex items-center gap-1.5">
                            {lead.score === 100 && <Flame className="h-4 w-4 text-gold animate-pulse" />}
                            {lead.name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{lead.zip_code || "—"}</td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-gold/40 transition-all ${tier.bg}`}
                                title={`${tier.label} lead — score ${lead.score}/100. Click for breakdown.`}
                              >
                                {lead.score >= 85 && <Flame className={`h-3.5 w-3.5 ${lead.score === 100 ? "animate-pulse" : ""}`} />}
                                <span className="uppercase tracking-wide">{tier.label}</span>
                                <span className="opacity-75">·</span>
                                <span>{lead.score}</span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <LeadScoreBreakdown lead={lead} />
                            </PopoverContent>
                          </Popover>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {(() => {
                            const ft = getFitTier(lead);
                            return ft ? <FitTierBadge tier={ft} /> : <span className="text-xs text-muted-foreground">—</span>;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{lead.acuity_notes || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{lead.payer_source || "—"}</td>
                        <td className="px-4 py-3">
                          <Badge variant={lead.status === "booked" ? "default" : "secondary"} className="text-xs capitalize">{lead.status.replace("_", " ")}</Badge>
                        </td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          {canAssign ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="inline-flex items-center gap-2 rounded px-2.5 py-1.5 sm:px-2 sm:py-1 hover:bg-muted shrink-0">
                                  {assignee ? (
                                    <>
                                      <span className="h-6 w-6 rounded-full bg-navy text-gold text-[10px] font-bold flex items-center justify-center shrink-0">{assignee.initials}</span>
                                      <span className="text-xs text-navy">{assignee.name}</span>
                                    </>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 text-warning px-2.5 py-1 sm:px-2 sm:py-0.5 text-xs">Unassigned</span>
                                  )}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-56 p-1" align="start">
                                <div className="text-[11px] font-semibold text-muted-foreground px-2 py-1.5 uppercase tracking-wide">Assign to</div>
                                {members.map(m => (
                                  <button
                                    key={m.id}
                                    onClick={() => assignLead(lead.id, m.id)}
                                    className={`w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm ${assignedId === m.id ? "text-gold font-medium" : "text-navy"}`}
                                  >
                                    {memberMap[m.id]?.name || m.id}
                                  </button>
                                ))}
                                {assignedId && (
                                  <button
                                    onClick={() => assignLead(lead.id, null)}
                                    className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm text-hot border-t mt-1"
                                  >
                                    Unassign
                                  </button>
                                )}
                              </PopoverContent>
                            </Popover>
                          ) : (
                            assignee ? (
                              <div className="inline-flex items-center gap-2 px-2.5 py-1.5 sm:px-2 sm:py-1 shrink-0">
                                <span className="h-6 w-6 rounded-full bg-navy text-gold text-[10px] font-bold flex items-center justify-center shrink-0">{assignee.initials}</span>
                                <span className="text-xs text-navy">{assignee.name}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground px-2.5">—</span>
                            )
                          )}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {lead.enriched ? (
                            <Badge variant="outline" className="text-xs gap-1 text-teal-600 border-teal-200"><Zap className="h-3 w-3" /> Enriched</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Pending</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setSelectedLead(lead)} className="p-1 rounded hover:bg-muted text-muted-foreground" title="View">
                              <Eye className="h-4 w-4" />
                            </button>
                            {canAssign && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="p-1 rounded hover:bg-muted text-muted-foreground" title="Assign">
                                    <UserPlus className="h-4 w-4" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 p-1" align="end">
                                  <div className="text-[11px] font-semibold text-muted-foreground px-2 py-1.5 uppercase tracking-wide">Assign to</div>
                                  {members.map(m => (
                                    <button
                                      key={m.id}
                                      onClick={() => assignLead(lead.id, m.id)}
                                      className={`w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm ${assignedId === m.id ? "text-gold font-medium" : "text-navy"}`}
                                    >
                                      {memberMap[m.id]?.name || m.id}
                                    </button>
                                  ))}
                                </PopoverContent>
                              </Popover>
                            )}
                            {profile?.booking_link && (
                              <button
                                onClick={() => openExternalUrl(profile.booking_link!)}
                                className="p-1 rounded hover:bg-muted text-muted-foreground"
                                title="Book"
                              >
                                <Calendar className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t">
              <span className="text-xs text-muted-foreground">{totalCount} total</span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
                  <span className="text-sm font-semibold text-navy">{page + 1}</span>
                  <span className="text-xs text-muted-foreground">of {totalPages}</span>
                  <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <LeadDrawer
        lead={selectedLead}
        calendarProvider={calendarProvider}
        bookingLink={profile?.booking_link}
        onClose={() => setSelectedLead(null)}
        onUpdate={async (id, updates) => {
          await updateLeadRecord(user?.id, id, updates);
          setSelectedLead(prev => (prev && prev.id === id ? { ...prev, ...updates } : prev));
          refetch();
        }}
      />
    </div>
  );
}
